import { ref } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { ExpertListingRequest } from "@shopexperts/shared";
import { isPlanLimitFeature, overPlanLimitGuidance } from "../utils/planLimitGuidance";

export interface PublishOptions {
  onBeforeInvalidate?: () => void;
  getPublishPayload: () => Partial<ExpertListingRequest> | null;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      errors?: Record<string, string[]>;
      error?: string;
      data?: {
        feature?: string;
        limit?: number;
        excess?: number;
      };
    };
  };
  message?: string;
}

export function useListingPublisher(options: PublishOptions) {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();
  const isPublishing = ref(false);
  const isPublishingProfile = ref(false);

  const publishMutation = useMutation({
    mutationKey: ["expert", "publishProfileDetails"],
    mutationFn: (payload: Partial<ExpertListingRequest>) => api.expert.saveProfileDetails({ body: payload }),
    onSuccess: async (_, payload) => {
      options?.onBeforeInvalidate?.();
      
      const invalidationPromises: Promise<void>[] = [];
      
      if (payload.profile || payload.offered_services) {
        invalidationPromises.push(
          queryClient.invalidateQueries({ queryKey: ["expert", "profileDetails"] })
        );
      }
      
      if (payload.packaged_services) {
        invalidationPromises.push(
          queryClient.invalidateQueries({ queryKey: ["expert", "packagedServices"] })
        );
      }
      
      if (payload.customer_stories) {
        invalidationPromises.push(
          queryClient.invalidateQueries({ queryKey: ["expert", "customerStories"] })
        );
      }
      
      if (payload.faqs) {
        invalidationPromises.push(
          queryClient.invalidateQueries({ queryKey: ["expert", "faqs"] })
        );
      }
      
      invalidationPromises.push(
        queryClient.invalidateQueries({ queryKey: ["expert", "me"] })
      );
      
      await Promise.all(invalidationPromises);
    },
    onError: (error) => {
      console.error('[useListingPublisher] Publish error:', error);
      console.error('[useListingPublisher] Error details:', {
        message: extractErrorMessage(error),
        response: (error as ApiError)?.response,
        fullError: error,
      });
    },
    onSettled: () => {
      isPublishing.value = false;
    }
  });

  const publishProfileMutation = useMutation({
    mutationKey: ["expert", "publishProfile"],
    mutationFn: () => api.expert.setProfileStatusToPublished(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["expert", "hasExpert"] });
    },
    onError: (error) => {
      console.error('[useListingPublisher] Publish profile error:', error);
      console.error('[useListingPublisher] Error details:', {
        message: extractErrorMessage(error),
        response: (error as ApiError)?.response,
        fullError: error,
      });
    },
    onSettled: () => {
      isPublishingProfile.value = false;
    }
  });

  function extractErrorMessage(error: unknown): string {
    const apiError = error as ApiError;

    const body = apiError?.response?.data;
    if (body?.error === 'plan_limit_exceeded' && isPlanLimitFeature(body.data?.feature)
      && typeof body.data.limit === 'number' && typeof body.data.excess === 'number') {
      return overPlanLimitGuidance(body.data.feature, body.data.limit, body.data.excess);
    }
    
    if (apiError?.response?.data?.message) {
      return apiError.response.data.message;
    }
    
    if (apiError?.response?.data?.errors) {
      const errors = apiError.response.data.errors;
      const firstError = Object.values(errors)[0];
      if (firstError && firstError.length > 0) {
        return firstError[0]!;
      }
    }
    
    if (apiError?.message) {
      return apiError.message;
    }
    
    return "Something went wrong. Please try again.";
  }

  async function publish() {
    isPublishing.value = true;
    
    const publishPayload = options.getPublishPayload();
    if (!publishPayload) {
      toaster.create({
        title: "No changes to save",
        type: "info",
      });
      isPublishing.value = false;
      return;
    }

    console.log('[useListingPublisher] Publishing with payload:', publishPayload);

    toaster.promise(
      publishMutation.mutateAsync(publishPayload),
      {
        loading: {
          title: "Saving your changes...",
          description: "Please wait while we update your listing",
        },
        success: {
          title: "Changes saved successfully",
          description: "Your listing has been updated",
        },
        error: (error: unknown) => {
          console.error('[useListingPublisher] Toast error:', error);
          return {
            title: "Failed to save changes",
            description: extractErrorMessage(error),
          };
        },
      }
    );
  }

  async function publishProfile() {
    isPublishingProfile.value = true;
    
    toaster.promise(
      publishProfileMutation.mutateAsync(),
      {
        loading: {
          title: "Publishing your profile...",
          description: "Please wait while we publish your profile",
        },
        success: {
          title: "Profile published successfully",
          description: "Your profile is now live and visible to clients",
        },
        error: (error: unknown) => ({
          title: "Failed to publish profile",
          description: extractErrorMessage(error),
        }),
      }
    );
  }

  return {
    publish,
    isPublishing,
    publishMutation,
    publishProfile,
    isPublishingProfile,
    publishProfileMutation
  };
}
